import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap, Polyline } from 'react-leaflet';
import { Box, Typography, Paper, Divider, Chip } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { renderToStaticMarkup } from 'react-dom/server';
import { Mountain } from 'lucide-react';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";
import CommonTable from "../../../common/CommonTable";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
});

const findNameFromCode = (nameMap, code) => nameMap[code] || code;

const SectionLabel = ({ text }) => (
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
        {text}
    </Typography>
);

const getPopupContent = (event, municipalityNames, subMunicipalityNames, sucoNames) => {
    const municipalityName = findNameFromCode(municipalityNames, event.municipality);
    const subMunicipalityName = findNameFromCode(subMunicipalityNames, event.sub_municipality);
    const sucoName = findNameFromCode(sucoNames, event.suco);

    return `
        <strong>Landslide Report</strong><br/>
        <hr style="margin: 4px 0;"/>
        <strong>Location:</strong> ${event.location}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Date of Event:</strong> ${event.date_landslide}<br/>
        <strong>Time of Event:</strong> ${event.time_landslide}<br/>
        <strong>Time Type:</strong> ${event.time_type}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Landslide Type:</strong> ${event.landslide_type}<br/>
        <strong>Dimensions:</strong> ${event.width}m x ${event.length}m<br/>
        <strong>Approximate Size:</strong> ${event.approximate_size}<br/>
        <strong>Materials:</strong> ${Array.isArray(event.materials) ? event.materials.join(', ') : event.materials}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Was Raining:</strong> ${event.was_raining}<br/>
        <strong>Rainfall Description:</strong> ${event.rainfall_description}<br/>
        <strong>Previous Landslides:</strong> ${event.previous_landslides}<br/>
        <strong>Apparent Cause:</strong> ${event.apparent_cause}<br/>
        <strong>Warning Signs:</strong> ${event.warning_signs}<br/>
        ${event.warning_signs === 'Yes' ? `<strong>Warning Description:</strong> ${event.warning_signs_description}<br/>` : ''}
        <hr style="margin: 4px 0;"/>
        <strong>Damage Caused:</strong> ${event.damage_caused}<br/>
        ${event.damage_caused === 'Yes' ? `<strong>Damage Description:</strong> ${event.damage_description}<br/>` : ''}
        <strong>Affected Status:</strong> ${event.affected_status}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Municipality:</strong> ${municipalityName}<br/>
        <strong>Sub-Municipality:</strong> ${subMunicipalityName}<br/>
        <strong>Suco:</strong> ${sucoName}<br/>
        <hr style="margin: 4px 0;"/>
        <strong>Additional Info:</strong> ${event.additional_info}<br/>
    `;
};

const createLandslideIcon = (color = '#2563eb', isSelected = false) => {
    const iconSvg = renderToStaticMarkup(<Mountain size={12} stroke="white" strokeWidth={2} />)
        .replace(/<svg/, `<svg width="12" height="12" viewBox="0 0 24 24"`);

    return L.divIcon({
        className: 'custom-div-icon',
        html: `
            <div style="position: relative; width: 20px; height: 20px;">
                <div style="
                    width: 20px; height: 20px; background-color: ${color}; border-radius: 50%;
                    position: absolute; top: -10px; left: -10px; z-index: 1;
                    display: flex; justify-content: center; align-items: center;
                    border: ${isSelected ? '2px solid white' : 'none'};
                    box-shadow: ${isSelected ? '0 0 8px rgba(0,0,0,0.5)' : 'none'};
                ">${iconSvg}</div>
                <div style="
                    width: 20px; height: 20px; background-color: ${color}; border-radius: 50%;
                    position: absolute; top: -10px; left: -10px; opacity: 0.4;
                    animation: pulse-ring 1.5s infinite; z-index: 0;
                "></div>
            </div>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
    });
};

const MapFlyToSelectedMarker = ({ selectedEvent, events, adm1Data, setSelectedEvent, municipalityNames, subMunicipalityNames, sucoNames }) => {
    const map = useMap();

    useEffect(() => {
        if (adm1Data) {
            const geojsonLayer = L.geoJSON(adm1Data);
            map.fitBounds(geojsonLayer.getBounds(), { padding: [50, 50] });
        }
    }, [adm1Data, map]);

    useEffect(() => {
        if (selectedEvent) {
            let bounds = null;
            if (selectedEvent.geojson_feature) {
                bounds = L.geoJSON(selectedEvent.geojson_feature).getBounds();
            } else if (selectedEvent.starting_point_lat) {
                bounds = L.latLngBounds([
                    [selectedEvent.starting_point_lat, selectedEvent.starting_point_long],
                    [selectedEvent.ending_point_lat, selectedEvent.ending_point_long]
                ]);
            } else if (selectedEvent.latitude && selectedEvent.longitude) {
                map.flyTo([selectedEvent.latitude, selectedEvent.longitude], 12, { animate: true, duration: 1.5 });
            }
            if (bounds) map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [selectedEvent, map]);

    const popupOptions = { maxHeight: 280, className: 'custom-leaflet-popup' };

    return (
        <>
            {adm1Data && (
                <GeoJSON data={adm1Data} style={() => ({ color: '#2563eb', fillOpacity: 0, weight: 2, opacity: 0.6 })} />
            )}
            {events && events.map((event, idx) => {
                const isSelected = selectedEvent?.id === event.id;
                const icon = createLandslideIcon(undefined, isSelected);
                const popupContent = getPopupContent(event, municipalityNames, subMunicipalityNames, sucoNames);

                if (event.geojson_feature) {
                    return (
                        <GeoJSON
                            key={idx}
                            data={event.geojson_feature}
                            style={() => ({
                                color: isSelected ? '#1d4ed8' : '#2563eb',
                                weight: isSelected ? 5 : 3,
                                opacity: isSelected ? 0.9 : 0.7,
                                fillColor: isSelected ? '#3b82f6' : '#60a5fa',
                                fillOpacity: isSelected ? 0.3 : 0.2
                            })}
                            onEachFeature={(feature, layer) => {
                                layer.bindPopup(popupContent, popupOptions);
                                layer.on('click', () => {
                                    setSelectedEvent(event);
                                    map.fitBounds(layer.getBounds(), { padding: [50, 50] });
                                });
                                if (isSelected) layer.openPopup();
                            }}
                        />
                    );
                } else if (event.starting_point_lat && event.starting_point_long) {
                    const latlngs = [
                        [event.starting_point_lat, event.starting_point_long],
                        [event.ending_point_lat, event.ending_point_long]
                    ];
                    return (
                        <Polyline
                            key={idx}
                            positions={latlngs}
                            color={isSelected ? '#1d4ed8' : '#2563eb'}
                            weight={isSelected ? 5 : 3}
                            opacity={isSelected ? 0.9 : 0.7}
                            eventHandlers={{ click: () => setSelectedEvent(event) }}
                        >
                            <Popup {...popupOptions}><div dangerouslySetInnerHTML={{ __html: popupContent }} /></Popup>
                        </Polyline>
                    );
                } else if (event.latitude && event.longitude) {
                    return (
                        <Marker key={idx} position={[event.latitude, event.longitude]} icon={icon} eventHandlers={{ click: () => setSelectedEvent(event) }}>
                            <Popup {...popupOptions}><div dangerouslySetInnerHTML={{ __html: popupContent }} /></Popup>
                        </Marker>
                    );
                }
                return null;
            })}
        </>
    );
};

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

function Reports() {
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [adm1Data, setAdm1Data] = useState(null);
    const [, setAdm2Data] = useState(null);
    const [, setAdm3Data] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [municipalityNames, setMunicipalityNames] = useState({});
    const [subMunicipalityNames, setSubMunicipalityNames] = useState({});
    const [sucoNames, setSucoNames] = useState({});

    useEffect(() => {
        if (!document.getElementById('landslide-pulse-animation')) {
            const style = document.createElement('style');
            style.id = 'landslide-pulse-animation';
            style.innerHTML = `
                @keyframes pulse-ring {
                    0% { transform: scale(0.5); opacity: 0.6; }
                    80%, 100% { transform: scale(2); opacity: 0; }
                }
                .custom-leaflet-popup .leaflet-popup-content-wrapper {
                    border-radius: 8px; padding: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                }
                .custom-leaflet-popup .leaflet-popup-content {
                    font-size: 13px; max-height: 280px; overflow-y: auto;
                    padding-right: 8px; width: auto; max-width: none;
                    color: #374151; line-height: 1.5;
                }
                .custom-leaflet-popup .leaflet-popup-content strong { color: #1e293b; font-weight: 600; }
                .custom-leaflet-popup .leaflet-popup-content hr { border: none; border-top: 1px solid #e2e8f0; margin: 6px 0; }
            `;
            document.head.appendChild(style);
        }
    }, []);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [geojson1, geojson2, geojson3] = await Promise.all([
                    axios.get('/DATA/OSMbased_adm1_boundary.json'),
                    axios.get('/DATA/OSMbased_adm2_boundary.json'),
                    axios.get('/DATA/OSMbased_adm3_boundary.json')
                ]);
                setAdm1Data(geojson1.data);
                setAdm2Data(geojson2.data);
                setAdm3Data(geojson3.data);

                const [muniRes, subMuniRes, sucoRes, reportsRes] = await Promise.all([
                    axios.post(`${backendUrl}Municipalities/municipalities_list/`),
                    axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
                    axios.post(`${backendUrl}Municipalities/suco_list/`),
                    axios.get(`${backendUrl}landslide/landslide-felt-reports/`)
                ]);

                setMunicipalityNames(muniRes.data.reduce((acc, c) => ({ ...acc, [c.adm1_code]: c.adm1 }), {}));
                setSubMunicipalityNames(subMuniRes.data.reduce((acc, c) => ({ ...acc, [c.adm2_code]: c.adm2 }), {}));
                setSucoNames(sucoRes.data.reduce((acc, c) => ({ ...acc, [c.adm3_code]: c.adm3 }), {}));
                setEvents(reportsRes.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
            }
        };
        fetchAllData();
    }, []);

    const tableRows = useMemo(() => events.map((event) => ({
        ...event,
        municipalityName: findNameFromCode(municipalityNames, event.municipality),
        subMunicipalityName: findNameFromCode(subMunicipalityNames, event.sub_municipality),
        sucoName: findNameFromCode(sucoNames, event.suco),
        locationDataType: event.geojson_feature ? 'Polygon' : event.starting_point_lat ? 'Two-Point Line' : 'Single Point',
        materialsText: Array.isArray(event.materials) ? event.materials.join(', ') : (event.materials || 'N/A'),
    })), [events, municipalityNames, subMunicipalityNames, sucoNames]);

    const columns = useMemo(() => [
        { key: 'date_landslide', label: 'Date', sortable: true },
        { key: 'time_landslide', label: 'Time' },
        { key: 'municipalityName', label: 'Municipality', sortable: true },
        { key: 'subMunicipalityName', label: 'Sub Municipality', sortable: true },
        { key: 'sucoName', label: 'Suco', sortable: true },
        { key: 'locationDataType', label: 'Location Type' },
        { key: 'location', label: 'Location' },
        { key: 'time_type', label: 'Time Type' },
        { key: 'landslide_type', label: 'Landslide Type', sortable: true },
        { key: 'width', label: 'Width', align: 'right' },
        { key: 'length', label: 'Length', align: 'right' },
        { key: 'approximate_size', label: 'Approx. Size' },
        { key: 'materialsText', label: 'Materials' },
        { key: 'was_raining', label: 'Was Raining' },
        { key: 'apparent_cause', label: 'Apparent Cause' },
        { key: 'warning_signs', label: 'Warning Signs' },
        { key: 'damage_caused', label: 'Damage Caused' },
        { key: 'affected_status', label: 'Affected Status' },
        { key: 'additional_info', label: 'Additional Info' },
    ], []);

    return (
        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
                            Landslide Felt Reports
                        </Typography>
                        {!loading && (
                            <Chip
                                label={`${events.length} ${events.length === 1 ? 'Report' : 'Reports'}`}
                                size="small"
                                sx={{ fontSize: '12px', fontWeight: 600, background: '#2563eb', color: '#fff', height: '22px' }}
                            />
                        )}
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                        View and explore landslide reports on map and table
                    </Typography>
                </Box>
            </Box>

            {/* Body */}
            <Box sx={{ p: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <Typography sx={{ fontSize: '15px', color: '#64748b' }}>Loading landslide reports...</Typography>
                    </Box>
                ) : (
                    <>
                        {/* Map Section */}
                        {events.length > 0 && (
                            <>
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                        <MapContainer center={[-8.8742, 125.7275]} zoom={8} style={{ height: '450px', width: '100%' }}>
                                          <MapHomeControl />
                                          <MapInfoControl text="Locations of submitted landslide reports. Click a marker to view report details." />
                                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                            <MapFlyToSelectedMarker
                                                selectedEvent={selectedEvent}
                                                events={events}
                                                adm1Data={adm1Data}
                                                setSelectedEvent={setSelectedEvent}
                                                municipalityNames={municipalityNames}
                                                subMunicipalityNames={subMunicipalityNames}
                                                sucoNames={sucoNames}
                                            />
                                        </MapContainer>
                                    </Box>
                                </Box>
                                <Divider sx={{ borderColor: '#e2e8f0', my: 3 }} />
                            </>
                        )}

                        {/* Table Section */}
                        <Box>
                            <SectionLabel text="Reports List" />

                            {events.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6, px: 3, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <Typography sx={{ fontSize: '15px', color: '#64748b', mb: 1 }}>No landslide reports available</Typography>
                                    <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>Reports will appear here once data is submitted</Typography>
                                </Box>
                            ) : (
                                <CommonTable
                                    tableData={tableRows}
                                    columns={columns}
                                    searchableKeys={['municipalityName', 'subMunicipalityName', 'sucoName', 'location', 'landslide_type', 'additional_info']}
                                    tableName="Landslide_Felt_Reports"
                                    emptyMessage="No landslide reports found."
                                    rowIdKey="id"
                                    highlightRowKey={selectedEvent?.id ?? null}
                                    onRowClick={(row) => setSelectedEvent(row)}
                                    defaultSort={{ key: 'date_landslide', direction: 'desc' }}
                                />
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </Paper>
    );
}

export default Reports;
